import React, { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ROLES, assignableRoles } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";

export { ROLES };

export const ClientPicker = ({ subAccounts, value, onChange }) => (
  <div className="space-y-1.5"><Label>Clients they can access</Label>
    <div className="flex flex-wrap gap-1.5">
      {subAccounts.map((s) => {
        const on = value.includes(s.id);
        return (
          <button key={s.id} type="button" onClick={() => onChange(on ? value.filter((x) => x !== s.id) : [...value, s.id])}
            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
            data-testid={`assign-sub-${s.id}`}>{s.name}</button>
        );
      })}
    </div></div>
);

export const RolePicker = ({ value, onChange }) => {
  const { user } = useAuth();
  return (
    <div className="space-y-1.5"><Label>Access level</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
        <SelectContent>{assignableRoles(user?.role).map((r) => <SelectItem key={r.value} value={r.value} data-testid={`role-option-${r.value}`}>{r.label}</SelectItem>)}</SelectContent>
      </Select></div>
  );
};

const EMPTY = { name: "", email: "", password: "", role: "subaccount_admin", sub_account_ids: [] };

export const AddMemberDialog = ({ open, onOpenChange, subAccounts, onAdded }) => {
  const [f, setF] = useState(EMPTY);
  const [mode, setMode] = useState("password");
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);

  const close = (v) => { onOpenChange(v); if (!v) { setF(EMPTY); setLink(null); } };
  const save = async () => {
    setSaving(true);
    try {
      if (mode === "password") {
        await api.post("/users", f);
        toast.success("Member added — share their email and temporary password");
        close(false);
      } else {
        const { password, ...rest } = f;
        const { data } = await api.post("/users/invite", { ...rest, send_email: true });
        setLink(data.invite_link);
        toast[data.email_sent ? "success" : "warning"](data.email_sent ? "Invite email sent" : "Email couldn't be sent — copy the link below");
      }
      onAdded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const valid = f.name && f.email && (mode === "invite" || f.password.length >= 8) && (f.role === "agency_admin" || f.sub_account_ids.length);
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="add-member-dialog">
        <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Invite link (valid 72 hours):</p>
            <Input readOnly value={link} className="font-mono text-xs" data-testid="invite-link-output" />
            <Button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} data-testid="copy-invite-link-button">Copy link</Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-2">
              {[["password", "Set a temporary password"], ["invite", "Email an invite link"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMode(k)} data-testid={`add-mode-${k}`}
                  className={`text-xs px-3 py-2 rounded-lg border ${mode === k ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
            {mode === "password" && <div className="space-y-1.5"><Label>Temporary password (min 8)</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /><p className="text-xs text-muted-foreground">They'll be asked to choose their own on first login.</p></div>}
            <RolePicker value={f.role} onChange={(role) => setF({ ...f, role })} />
            {f.role !== "agency_admin" && <ClientPicker subAccounts={subAccounts} value={f.sub_account_ids} onChange={(ids) => setF({ ...f, sub_account_ids: ids })} />}
          </div>
        )}
        {!link && <DialogFooter><Button onClick={save} disabled={saving || !valid} data-testid="save-user-button">{mode === "password" ? "Add member" : "Send invite"}</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
